import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { OrderCenterAction } from '@models/dashboard.model';
import { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'ts-carousel',
  imports: [AsButtonDirective],
  templateUrl: './carousel.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CarouselComponent implements AfterViewInit {
  @ViewChild('swiperContainer') swiper!: ElementRef;
  @Input() order_center_actions: OrderCenterAction[] = [];
  @Output() item_click = new EventEmitter<any>();
  show_nav_btns: boolean;
  swiper_options: SwiperOptions = {
    observer: true,
    observeParents: true,
    slidesPerView: 6,
    slidesPerGroup: 6,
    loop: true,
    watchOverflow: true,
    longSwipesRatio: 0.1,
    navigation: false, // disabled — using custom buttons instead
    pagination: { enabled: true, clickable: true, lockClass: 'pb-3' },
    keyboard: true,
    spaceBetween: 25,
    grabCursor: true,
    autoHeight: false,
    injectStyles: [
      `
      /* While dragging = grabbing everywhere */
      :host(.is-dragging) .swiper,
      :host(.is-dragging) .swiper-wrapper,
      :host(.is-dragging) .swiper-slide,
      :host(.is-dragging) .swiper-slide * {
        cursor: grabbing !important;
      }

      :host .swiper {
        padding: .125rem 0;
        overflow: visible !important;

        &:focus, &:focus-visible {
          outline: 0;
        }
      }

      :host .swiper-pagination {
        position: relative !important;
        margin-top: 1rem;
      }

      :host .swiper-pagination-bullet {
        width: 2rem;
        height: .5rem;
        background: #ced4da;
        opacity: 1;
        border-radius: 9999px;
        border: none;
        padding: 0;
        cursor: pointer;
        transition: background-color 0.2s ease;
        margin: 0;
      }

      :host .swiper-pagination-bullet-active {
        background: #157db9;
        width: 2rem;
        height: .5rem;
        border-radius: 9999px;
      }
      `
    ],
    breakpoints: {
      0: { slidesPerView: 2, slidesPerGroup: 2 },
      576: { slidesPerView: 3, slidesPerGroup: 3 },
      768: { slidesPerView: 4, slidesPerGroup: 4 },
      992: { slidesPerView: 5, slidesPerGroup: 5 },
      1200: { slidesPerView: 6, slidesPerGroup: 6 }
    },
    on: {
      lock: () => {
        this.show_nav_btns = false;
        this.cdr.detectChanges();
      },
      unlock: () => {
        this.show_nav_btns = true;
        this.cdr.detectChanges();
      }
    }
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.initializeSwiper();
    this.observeActionCardHeight();
  }

  slidePrev() {
    this.swiper.nativeElement.swiper.slidePrev();
  }

  slideNext() {
    this.swiper.nativeElement.swiper.slideNext();
  }

  onItemClick(item: OrderCenterAction): void {
    this.item_click.emit(item);
  }

  private initializeSwiper(): void {
    const swiper_native_el: any = this.swiper?.nativeElement;
    Object.assign(swiper_native_el, this.swiper_options);

    swiper_native_el.initialize();

    swiper_native_el.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') this.swiper.nativeElement.swiper.slideNext();
      if (e.key === 'ArrowLeft') this.swiper.nativeElement.swiper.slidePrev();
    });
    swiper_native_el.swiper.on('touchStart', () => {
      if (!this.swiper.nativeElement.swiper.isLocked)
        this.swiper.nativeElement.classList.add('is-dragging');
    });
    swiper_native_el.swiper.on('touchEnd', () => {
      if (!this.swiper.nativeElement.swiper.isLocked)
        this.swiper.nativeElement.classList.remove('is-dragging');
    });
  }

  private observeActionCardHeight(): void {
    if (document.readyState === 'complete') {
      const actionCard: HTMLElement = document.getElementsByClassName(
        'order-action-card'
      )[0] as HTMLElement;
      if (!actionCard) return;

      this.updateNavBtnsTop(actionCard);

      const resize_observer: ResizeObserver = new ResizeObserver(() => {
        this.updateNavBtnsTop(actionCard);
      });

      resize_observer.observe(actionCard);
    }
  }

  private updateNavBtnsTop(action_card: HTMLElement): void {
    const card_height: number = action_card.offsetHeight;
    const button_top: number = (card_height - 32) / 2; // Center vertically

    const prev_btn: HTMLElement = document.getElementsByClassName(
      'swiper-btn-prev'
    )[0] as HTMLElement;
    const next_btn: HTMLElement = document.getElementsByClassName(
      'swiper-btn-next'
    )[0] as HTMLElement;

    if (prev_btn) prev_btn.style.setProperty('top', `${button_top}px`);
    if (next_btn) next_btn.style.setProperty('top', `${button_top}px`);
  }
}
